import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';

import { editarBreadcrumb } from '../../actions/breadcrumb';
import {
    cargarCategoriasInventario,
    cargarProveedoresInventario,
    listarInventario,
    limpiarFiltrosInventario,
    setFiltrosInventario,
    cargarDetallesInventario,
    limpiarDetallesInventario
} from '../../actions/inventario';

import FiltrosAvanzadosModal from './modals/FiltrosAvanzadosModal';
import StockProductoModal from './modals/StockProductoModal';

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '$ 0.00';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
};

const Inventario = () => {
    const dispatch = useDispatch();

    const { listado, filtros } = useSelector((state) => state.inventario);

    const [busquedaGeneral, setBusquedaGeneral] = useState('');
    const [modalFiltros, setModalFiltros] = useState(false);
    const [modalStock, setModalStock] = useState(false);
    const [productoStock, setProductoStock] = useState(null);

    useEffect(() => {
        const rutasBread = [
            { activo: true, nombre: 'Inventario', path: '/inventario' }
        ];
        dispatch(editarBreadcrumb(rutasBread));
        dispatch(listarInventario({}));
        dispatch(cargarCategoriasInventario());
        dispatch(cargarProveedoresInventario());
    }, [dispatch]);

    const hayFiltros = useMemo(() => {
        return Boolean(filtros?.id_categoria || filtros?.id_subcategoria || filtros?.id_proveedor);
    }, [filtros]);

    const listadoFiltrado = useMemo(() => {
        const q = (busquedaGeneral || '').trim().toLowerCase();
        if (!q) return listado || [];

        return (listado || []).filter((p) => {
            const codigo = (p?.codigo_tienda || '').toString().toLowerCase();
            const nombre = (p?.nombre || '').toString().toLowerCase();
            const cat = (p?.categoria?.nombre || '').toString().toLowerCase();
            const sub = (p?.subcategoria?.nombre || '').toString().toLowerCase();
            const codigoProv = (p?.codigo_proveedor || '').toString().toLowerCase();

            return (
                codigo.includes(q) ||
                nombre.includes(q) ||
                cat.includes(q) ||
                sub.includes(q) ||
                (filtros?.id_proveedor ? codigoProv.includes(q) : false)
            );
        });
    }, [listado, busquedaGeneral, filtros]);

    const onOpenStock = async (row) => {
        await dispatch(limpiarDetallesInventario());
        await dispatch(cargarDetallesInventario(row?._id));
        setModalStock(true);
    };

    const onLimpiarFiltros = async () => {
        await dispatch(limpiarFiltrosInventario());
        await dispatch(setFiltrosInventario({
            id_categoria: '',
            id_subcategoria: '',
            id_proveedor: ''
        }));
        await dispatch(listarInventario({}));
    };

    const columnsBase = [
        {
            name: 'Código tienda',
            selector: (row) => row?.codigo_tienda || 'Sin definir',
            sortable: true,
            wrap: true
        },
        {
            name: 'Nombre',
            selector: (row) => row?.nombre || 'Sin definir',
            sortable: true,
            grow: 2,
            wrap: true
        },
        {
            name: 'Categoría',
            selector: (row) => row?.categoria?.nombre || 'Sin definir',
            sortable: true,
            wrap: true
        },
        {
            name: 'Subcategoría',
            selector: (row) => row?.subcategoria?.nombre || 'Sin definir',
            sortable: true,
            wrap: true
        }
    ];

    const columnsProveedor = [
        {
            name: 'Código proveedor',
            selector: (row) => row?.codigo_proveedor || 'Sin definir',
            sortable: true,
            wrap: true
        },
        {
            name: 'P. compra',
            selector: (row) => formatMoney(row?.precio_historico),
            sortable: true,
            wrap: true
        },
        {
            name: 'P. público',
            selector: (row) => formatMoney(row?.precio_publico),
            sortable: true,
            wrap: true
        },
        {
            name: 'P. mínimo',
            selector: (row) => formatMoney(row?.precio_minimo),
            sortable: true,
            wrap: true
        },
        {
            name: 'P. liquidación',
            selector: (row) => formatMoney(row?.precio_liquidacion),
            sortable: true,
            wrap: true
        }
    ];

    const columnCantidad = [
        {
            name: 'Cantidad actual',
            sortable: true,
            right: true,
            cell: (row) => {
                const cantidad = Number(row?.cantidad || 0);
                const className = cantidad > 0 ? 'btn btn-sm btn-success' : 'btn btn-sm btn-warning';
                return (
                    <button
                        type="button"
                        className={className}
                        onClick={() => onOpenStock(row)}
                    >
                        {cantidad}
                    </button>
                );
            }
        }
    ];

    const columns = useMemo(() => {
        if (filtros?.id_proveedor) {
            return [...columnsBase, ...columnsProveedor, ...columnCantidad];
        }
        return [...columnsBase, ...columnCantidad];
    }, [filtros]);

    const subHeaderComponent = (
        <div className="d-flex w-100 justify-content-between align-items-center gap-2">
            <div className="flex-grow-1">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Buscar..."
                    value={busquedaGeneral}
                    onChange={(e) => setBusquedaGeneral(e.target.value)}
                />
            </div>

            {hayFiltros && (
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onLimpiarFiltros}
                >
                    Limpiar filtros
                </button>
            )}

            <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setModalFiltros(true)}
            >
                Filtros avanzados
            </button>
        </div>
    );

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1">
                    <h3 className="mb-4">Inventario de productos</h3>

                    <DataTable
                        columns={columns}
                        data={listadoFiltrado}
                        pagination
                        highlightOnHover
                        responsive
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                    />
                </div>
            </div>

            <FiltrosAvanzadosModal
                show={modalFiltros}
                onHide={() => setModalFiltros(false)}
            />

            <StockProductoModal
                show={modalStock}
                onHide={() => {
                    setModalStock(false);
                    dispatch(limpiarDetallesInventario());
                }}
                producto={productoStock}
            />
        </Fragment>
    );
};

export default Inventario;